import { fg } from '@atlassian/jira-feature-gating';

import type {
	OptimisticFieldSet,
	PriorityOptimisticResponse,
	UserOptimisticResponse,
	StatusOptimisticResponse,
	SelectOptimisticResponse,
	SprintOptimisticResponse,
	NumberOptimisticResponse,
	LabelOptimisticResponse,
	MultiGroupOptimisticResponse,
	MultiVersionOptimisticResponse,
} from './types.tsx';

export const toOptimisticPriority = ({
	issueId,
	fieldId,
	fieldType,
	value: { priority, fieldSetId },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: PriorityOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		fieldSetId: fieldSetId && fg('group_by_any_fields_technical') ? fieldSetId : fieldType,
		type: fieldId,
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraPriorityField',
						__isJiraIssueField: 'JiraPriorityField',
						fieldId,
						id: `${issueId}/${fieldId}`,
						type: fieldType,
						name: 'Priority',
						priority,
					},
				},
			],
		},
	},
});

export const toOptimisticUser = ({
	issueId,
	fieldId,
	fieldType,
	value: { user, fieldSetId },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: UserOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId: fieldSetId && fg('group_by_any_fields_technical') ? fieldSetId : fieldId,
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraSingleSelectUserPickerField',
						__isJiraIssueField: 'JiraSingleSelectUserPickerField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: fg('group_by_any_fields_technical') ? 'User' : 'Assignee',
						user: user
							? {
									__typename: 'AtlassianAccountUser',
									accountStatus: 'active',
									id: user.id,
									accountId: user.accountId,
									picture: user.picture,
									name: user.name,
								}
							: null,
					},
				},
			],
		},
	},
});

export const toOptimisticStatus = ({
	issueId,
	fieldId,
	fieldType,
	value: { status, fieldSetId },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: StatusOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId: fieldSetId && fg('group_by_any_fields_technical') ? fieldSetId : fieldId,
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraStatusField',
						__isJiraIssueField: 'JiraStatusField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						status,
					},
				},
			],
		},
	},
});

export const toOptimisticSingleSelect = ({
	issueId,
	fieldId,
	fieldType,
	value: { option, fieldSetId },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: SelectOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId:
			fieldSetId && fg('group_by_any_fields_technical') ? fieldSetId : 'Category[Category]',
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraSingleSelectField',
						__isJiraIssueField: 'JiraSingleSelectField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: fg('group_by_any_fields_technical') ? 'SingleSelectCustomField' : 'Category',
						fieldOptions: {
							totalCount: 1,
						},
						fieldOption: option
							? {
									id: option.id,
									optionId: option.optionId,
									value: option.value,
									color: option.color,
								}
							: null,
					},
				},
			],
		},
	},
});

export const toOptimisticRadioSelect = ({
	issueId,
	fieldId,
	fieldType,
	value: { option, fieldSetId },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: SelectOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId,
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraRadioSelectField',
						__isJiraIssueField: 'JiraRadioSelectField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: 'RadioSelectField',
						selectedOption: option
							? {
									id: option.id,
									// NOTE: For radio select fields, the value is the id of the option
									value: option.id,
									label: option.value,
								}
							: null,
					},
				},
			],
		},
	},
});

export const toOptimisticMultiSelect = ({
	issueId,
	fieldId,
	fieldType,
	value: { option, fieldSetId },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: SelectOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId,
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraMultipleSelectField',
						__isJiraIssueField: 'JiraMultipleSelectField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: 'MultiSelectCustomField',
						selectedOptions: {
							edges: option
								? [
										{
											node: {
												id: option.id,
												optionId: option.optionId,
												value: option.value,
											},
										},
									]
								: [],
						},
					},
				},
			],
		},
	},
});

export const toOptimisticCheckbox = ({
	issueId,
	fieldId,
	fieldType,
	value: { option, fieldSetId },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: SelectOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId,
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraCheckboxesField',
						__isJiraIssueField: 'JiraCheckboxesField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: 'MultiSelectCustomField',
						selectedOptions: {
							edges: option
								? [
										{
											node: {
												id: option.id,
												value: option.value,
											},
										},
									]
								: [],
						},
					},
				},
			],
		},
	},
});

export const toOptimisticSprint = ({
	issueId,
	fieldId,
	fieldType,
	value: { sprint, fieldSetId },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: SprintOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId: fieldSetId && fg('group_by_any_fields_technical') ? fieldSetId : fieldId,
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraSprintField',
						__isJiraIssueField: 'JiraSprintField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: 'Sprint',
						selectedSprintsConnection: {
							edges: sprint
								? [
										{
											node: {
												id: sprint.id,
												sprintId: sprint.sprintId,
												name: sprint.name,
												state: sprint.state,
												endDate: sprint.endDate,
											},
										},
									]
								: [],
						},
					},
				},
			],
		},
	},
});

export const toOptimisticNumber = ({
	issueId,
	fieldId,
	fieldType,
	value: { value, fieldSetId, isStoryPointField },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: NumberOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId,
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraNumberField',
						__isJiraIssueField: 'JiraNumberField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: 'Story point estimate',
						number: value,
						isStoryPointField,
					},
				},
			],
		},
	},
});

export const toOptimisticLabels = ({
	issueId,
	fieldId,
	fieldType,
	value: { value, fieldSetId },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: LabelOptimisticResponse;
}): OptimisticFieldSet => {
	return {
		node: {
			type: fieldType,
			fieldSetId,
			fields: {
				totalCount: 1,
				edges: [
					{
						node: {
							__typename: 'JiraLabelsField',
							__isJiraIssueField: 'JiraLabelsField',
							fieldId,
							type: fieldType,
							id: `${issueId}/${fieldId}`,
							name: 'Labels',
							selectedLabelsConnection: {
								totalCount: value ? 1 : 0,
								edges: value
									? [
											{
												node: {
													name: value,
													color: null,
												},
											},
										]
									: [],
							},
						},
					},
				],
			},
		},
	};
};

export const toOptimisticMultiGroup = ({
	issueId,
	fieldId,
	fieldType,
	value: { value, fieldSetId },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: MultiGroupOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId,
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraMultipleGroupPickerField',
						__isJiraIssueField: 'JiraMultipleGroupPickerField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: 'Groups',
						selectedGroupsConnection: {
							edges: value
								? [
										{
											node: value,
										},
									]
								: [],
						},
					},
				},
			],
		},
	},
});

export const toOptimisticMultiVersion = ({
	issueId,
	fieldId,
	fieldType,
	value: { version, fieldSetId },
}: {
	issueId: string;
	fieldId: string;
	fieldType: string;
	value: MultiVersionOptimisticResponse;
}): OptimisticFieldSet => ({
	node: {
		type: fieldType,
		fieldSetId,
		fields: {
			totalCount: 1,
			edges: [
				{
					node: {
						__typename: 'JiraMultipleVersionPickerField',
						__isJiraIssueField: 'JiraMultipleVersionPickerField',
						fieldId,
						type: fieldType,
						id: `${issueId}/${fieldId}`,
						name: 'Versions',
						selectedVersionsConnection: {
							edges: version
								? [
										{
											node: {
												id: version.id,
												value: version.id,
												name: version.name,
												label: version.name,
												versionId: version.versionId,
											},
										},
									]
								: [],
						},
					},
				},
			],
		},
	},
});
